// Production.js — Admin-like layout + 4-col responsive grid + fast paging + user identity cards
// ✅ Keeps existing business logic (visibility rules, roles, routes, request-access, delete confirm) intact
// ✅ Avoids any impact on Navbar/Sidebar submenu by:
//    - never using full-viewport clickable overlays
//    - scoping all CSS under .prod-pin
//    - using a fixed topbar that never blocks clicks outside its inner controls (pointer-events: none on wrapper)
//    - keeping z-index below the navbar layer

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Tooltip } from "bootstrap";

const PAGE_SIZE = 24;
const WV_LANG_KEY = "weavevo_lang";

const PROD_I18N = {
  en: {
    all: "All",
    draft: "Draft",
    active: "In the Loom",
    done: "Finished weaves",
    none: "No projects",
    actions: "Actions",
    public: "Public project",
    private: "Private project",
    createdBy: "Created by",
    on: "on",
    sincePrefix: "since",
    editScenario: "Edit scenario",
    delete: "Delete",
    deleteConfirmHint: 'Click "Delete" again within 5 seconds to confirm.',
    openStudio: "Open studio",
    openRecap: "Open recap",
    restricted: "Restricted access — Request access",
    requestSent: "✅ Request sent",
    requestAccess: "Request access",
    noActions: "No actions",
    loadMore: "Load more",
    loading: "Loading…",
    titleFallback: "Untitled",
    descFallback: "—",
  },
  fr: {
    all: "Tous",
    draft: "Draft",
    active: "In the Loom",
    done: "Finished weaves",
    none: "Aucun projet",
    actions: "Actions",
    public: "Projet public",
    private: "Projet privé",
    createdBy: "Créé par",
    on: "le",
    sincePrefix: "depuis",
    editScenario: "Modifier le scénario",
    delete: "Supprimer",
    deleteConfirmHint: 'Cliquez à nouveau sur "Supprimer" dans les 5 secondes pour confirmer.',
    openStudio: "Ouvrir le studio",
    openRecap: "Ouvrir le récap",
    restricted: "Accès restreint — Demandez un accès",
    requestSent: "✅ Demande envoyée",
    requestAccess: "Demander un accès",
    noActions: "Aucune action",
    loadMore: "Charger plus",
    loading: "Chargement…",
    titleFallback: "Sans titre",
    descFallback: "—",
  },
};

const getLang = () => {
  const ls = (typeof window !== "undefined" && window.localStorage && localStorage.getItem(WV_LANG_KEY)) || "";
  const ss = (typeof window !== "undefined" && window.sessionStorage && sessionStorage.getItem(WV_LANG_KEY)) || "";
  const v = String(ls || ss || "en").toLowerCase();
  return v.startsWith("fr") ? "fr" : "en";
};



const MEDIA_MAX_HEIGHT = 360;

const makeSeedImage = (title, id, w = 640, h = 360) =>
  `https://picsum.photos/seed/${encodeURIComponent((title || "Project") + "-" + id)}/${w}/${h}`;

/** small helper to avoid uncontrolled external URLs in /uploads */
const normalizeUploadPath = (p) => {
  let s = String(p || "");
  if (!s) return "";

  // absolute / special URLs
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  )
    return s;

  // already normalized
  if (s.startsWith("/uploads/")) return s;
  if (s.startsWith("uploads/")) return "/" + s;

  while (s.startsWith("/")) s = s.slice(1);
  return "/uploads/" + s;
};

const appendBust = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("v", String(Date.now()));
    return u.toString();
  } catch {
    return url;
  }
};

const useInViewOnce = (rootMargin = "700px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
};

const styles = `
/* -------------------- Page base (scoped) -------------------- */
.prod-pin, .prod-pin *, .prod-pin *::before, .prod-pin *::after { box-sizing: border-box; }

.prod-pin.page-wrap{
  --wv-content-pad: 6px;
  --wv-rail-w-local: var(--wv-rail-w, 125px);      /* read global if set */
  --wv-navbar-h-local: var(--wv-navbar-h, 70px);   /* read global if set */
  background: #fafafa;
  min-height: 100vh;

  /* Match Admin-like spacing without touching Navbar */
  padding-top:  142px;
  padding-left: 20px;
  padding-right: var(--wv-content-pad) !important;
}

@media (max-width: 1100px){
  .prod-pin.page-wrap{ --wv-content-pad: 6px; }
}
@media (max-width: 720px){
  .prod-pin.page-wrap{
    --wv-rail-w-local: 0px;
    --wv-content-pad: 8px;
    padding-top: calc(var(--wv-navbar-h-local) + 64px);
  }
}

/* -------------------- Topbar (safe for navbar/submenu) -------------------- */
/* Wrapper covers the screen width, but NEVER blocks clicks outside its inner controls */
.prod-pin .wv-topbar{
  position: fixed;
  top: var(--wv-navbar-h-local);
  left: 0;
  right: 0;
  z-index: 120;                 /* keep it BELOW typical navbar/dropdown layers */
  pointer-events: none;         /* ✅ critical: never intercept clicks (sidebar/nav/submenu) */
}

.prod-pin .wv-topbar-inner{
  pointer-events: auto;         /* ✅ only controls are clickable */
  margin-left: var(--wv-rail-w-local);
  width: calc(100dvw - var(--wv-rail-w-local));
  max-width: calc(100vw - var(--wv-rail-w-local));
  background: rgba(250,250,250,.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0,0,0,.06);
  padding: 8px var(--wv-content-pad);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.prod-pin .wv-topbar-spacer{
  height: 62px; /* must roughly match inner height to avoid content hidden */
}

/* -------------------- Filter pills -------------------- */
.prod-pin .filter-pill{
  border: 0; border-radius: 9999px;
  padding: .55rem .95rem;
  background: #f3f4f6; color: #111;
  font-weight: 500; font-size:13px; line-height: 1;
  display: inline-flex; align-items: center; gap: .5rem;
  cursor: pointer; user-select: none; white-space: nowrap;
}
.prod-pin .filter-pill .bi { font-size: 1rem; }
.prod-pin .filter-pill:hover { background: #e9ecef; }
.prod-pin .filter-pill:focus-visible { outline: 2px solid #0d6efd; outline-offset: 2px; }
.prod-pin .filter-pill.active { background: #111; color: #fff; }

/* -------------------- Masonry (CSS columns) -------------------- */
.prod-pin .prod-main{
  width: 100%;
}

.prod-pin .prod-masonry{
  width: 100%;
  max-width: 100%;
  column-count: 4;
  column-gap: 8px;
}

@media (max-width: 1400px){
  .prod-pin .prod-masonry{ column-count: 3; }
}
@media (max-width: 980px){
  .prod-pin .prod-masonry{ column-count: 2; }
}
@media (max-width: 540px){
  .prod-pin .prod-masonry{ column-count: 1; }
}

.prod-pin .prod-masonry-item{
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
  display: inline-block;
  width: 100%;
  margin: 0 0 8px;
}

/* -------------------- Card -------------------- */
.prod-pin .prod-card{
  border: 1px solid rgba(0,0,0,.07);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(0,0,0,.06);
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: visible;
}
.prod-pin .prod-card:hover{ box-shadow: 0 18px 48px rgba(0,0,0,.10); }

.prod-pin .prod-top{
  padding: 8px 8px 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.prod-pin .prod-bottom{
  padding: 6px 8px 8px;
}

.prod-pin .prod-title{
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Media box */
.prod-pin .prod-media-box{
  width: 100%;
  max-height: ${MEDIA_MAX_HEIGHT}px;
  background: #0b0c0d;
  border-radius: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.prod-pin .prod-media{
  width: 100%;
  height: auto;
  max-height: ${MEDIA_MAX_HEIGHT}px;
  object-fit: contain;
  background: #0b0c0d;
}
.prod-pin .prod-media--video{ pointer-events: none; }

/* Identity line (avatar + name) */
.prod-pin .prod-identity{
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  margin-top: 2px;
}
.prod-pin .prod-avatar{
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #e9ecef;
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,.10);
}
.prod-pin .prod-avatar img{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.prod-pin .prod-name{
  display: inline-flex;
  gap: 6px;
  align-items: baseline;
  min-width: 0;
}
.prod-pin .prod-username{
  font-weight: 700;
  color: #111;
  cursor: pointer;
  text-decoration: none;
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.prod-pin .prod-username:hover{ text-decoration: underline; }
.prod-pin .prod-date{
  color: rgba(0,0,0,.55);
  font-weight: 600;
  font-size: .88rem;
  white-space: nowrap;
}

/* Visibility badge */
.prod-pin .prod-vis-badge{
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,.08);
  background: rgba(255,255,255,.9);
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Actions (3 dots) */
.prod-pin .prod-actions-trigger{
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0,0,0,.08);
  background: rgba(255,255,255,.9);
  box-shadow: 0 6px 18px rgba(0,0,0,.10);
  font-family: inherit;
  font-size: 1rem;
}
.prod-pin .prod-actions-trigger .bi{ font-size: 1.1rem; }
.prod-pin .prod-actions-trigger:hover{ background: #fff; }

.prod-pin .prod-actions-menu{
  font-family: inherit;
  font-size: 1rem;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 18px;
  box-shadow: 0 18px 46px rgba(0,0,0,.18);
  padding: .35rem;
  min-width: 220px;
  max-width: 260px;
  z-index: 300; /* still below navbar dropdowns typically */
}

.prod-pin .prod-dd-item{
  display: flex;
  align-items: center;
  gap: .65rem;
  padding: .55rem .70rem;
  border-radius: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.prod-pin .prod-dd-item .bi{
  font-size: 1.05rem;
  width: 1.2rem;
  text-align: center;
  opacity: .9;
}
.prod-pin .prod-dd-item:hover{ background: rgba(0,0,0,.05); }
.prod-pin .prod-dd-item:active{ background: rgba(0,0,0,.08); }
.prod-pin .prod-dd-item.danger{ color: #dc3545; }
.prod-pin .prod-dd-item.danger:hover{ background: rgba(220,53,69,.08); }

.prod-pin .prod-dd-sep{
  height: 1px;
  background: rgba(0,0,0,.06);
  margin: .35rem .35rem;
}

/* -------------------- Skeleton -------------------- */
.prod-pin .skel{
  border: 1px solid rgba(0,0,0,.07);
  border-radius: 18px;
  background: #fff;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,.06);
}
.prod-pin .skel-line,
.prod-pin .skel-box{
  background: linear-gradient(90deg, rgba(0,0,0,.06), rgba(0,0,0,.10), rgba(0,0,0,.06));
  background-size: 200% 100%;
  animation: skel 1.1s ease-in-out infinite;
  border-radius: 12px;
}
.prod-pin .skel-line{ height: 12px; margin: 8px 0; }
.prod-pin .skel-line.sm{ width: 55%; }
.prod-pin .skel-line.md{ width: 80%; }
.prod-pin .skel-line.lg{ width: 95%; }
.prod-pin .skel-box{ height: 210px; margin-top: 10px; }

@keyframes skel{
  0%{ background-position: 200% 0; }
  100%{ background-position: -200% 0; }
}
`;

const SkeletonCard = React.memo(function SkeletonCard() {
  return (
    <div className="prod-masonry-item">
      <div className="skel">
      <div className="d-flex align-items-center justify-content-between">
        <div className="skel-line md" />
        <div className="skel-line sm" style={{ width: 44, height: 12 }} />
      </div>
      <div className="skel-line lg" />
      <div className="skel-line md" />
      <div className="skel-box" />
      <div className="d-flex align-items-center mt-3" style={{ gap: 10 }}>
        <div className="skel-line sm" style={{ width: 28, height: 28, borderRadius: 999 }} />
        <div className="skel-line sm" style={{ width: 140 }} />
      </div>
    </div>
    </div>
  );
});

const LazyMedia = React.memo(function LazyMedia({ kind, src, poster, hoverAutoplay }) {
  const [ref, inView] = useInViewOnce("900px");
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (kind !== "video") return;
    const v = videoRef.current;
    if (!v) return;

    // Stop playback when not hovered or feature disabled
    if (!hoverAutoplay || !hovered || !inView) {
      try {
        v.pause();
        v.currentTime = 0;
      } catch {
        // ignore
      }
      return;
    }

    // Start playback on hover (muted for reliable autoplay)
    try {
      v.muted = true;
      v.playsInline = true;
      v.loop = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore
    }
  }, [kind, hoverAutoplay, hovered, inView, src]);

  return (
    <div
      ref={ref}
      className="prod-media-box"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      {!inView ? (
        <div className="skel-box" style={{ height: 210, width: "100%", borderRadius: 14, marginTop: 0 }} />
      ) : kind === "video" ? (
        <video
          ref={videoRef}
          className="prod-media prod-media--video"
          preload="metadata"
          poster={poster || undefined}
          muted
          playsInline
        >
          <source src={src} type="video/mp4" />
        </video>
      ) : (
        <img className="prod-media" src={src} alt="Project" loading="lazy" decoding="async" />
      )}
    </div>
  );
});

const Production = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // language (same storage key as Admin)
  const [lang, setLang] = useState(getLang());
  useEffect(() => {
    const onStorage = () => setLang(getLang());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const t = useCallback((k) => PROD_I18N[lang]?.[k] ?? PROD_I18N.en[k] ?? k, [lang]);

  // Mode (keep existing logic)
  const searchParams = new URLSearchParams(location.search);
  const modeRaw = (searchParams.get("mode") || "").toLowerCase();
  const portfolioMode = modeRaw === "master" || modeRaw === "contrib" ? modeRaw : "all";

  const [activeFilter, setActiveFilter] = useState("All");

  const [projects, setProjects] = useState([]);
  const [projectsAllFallback, setProjectsAllFallback] = useState(null); // if backend doesn't support paging

  const [rolesMap, setRolesMap] = useState({});
  const [requestSentProjects, setRequestSentProjects] = useState({});
  const [pendingDelete, setPendingDelete] = useState({ id: null, expiresAt: 0 });

  const [username, setUsername] = useState("");
  const [userGroups, setUserGroups] = useState([]);

  const [usersByUsername, setUsersByUsername] = useState({});
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [isBootLoading, setIsBootLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState("");

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const pagingSupportedRef = useRef(true);
  const isLoadingRef = useRef(false);

  // decode user
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setUsername(decoded?.preferred_username || "");
      setUserGroups(decoded?.groups || []);
    } catch {
      setUsername("");
      setUserGroups([]);
    }
  }, [token]);

  // format helpers
  const formatUsernameLabel = useCallback((raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    return s
      .split(/[._\-\s]+/g)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const formatSince = useCallback(
    (d) => {
      const dt = d ? new Date(d) : null;
      if (!dt || Number.isNaN(dt.getTime())) return "";
      const now = Date.now();
      let diff = now - dt.getTime();
      if (diff < 0) diff = 0;

      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;
      const week = 7 * day;
      const month = 30 * day;
      const year = 365 * day;

      const p = String(t("sincePrefix") || "since").toLowerCase();

      const units =
        lang === "fr"
          ? {
              minute: ["minute", "minutes"],
              hour: ["heure", "heures"],
              day: ["jour", "jours"],
              week: ["semaine", "semaines"],
              month: ["mois", "mois"],
              year: ["an", "ans"],
            }
          : {
              minute: ["minute", "minutes"],
              hour: ["hour", "hours"],
              day: ["day", "days"],
              week: ["week", "weeks"],
              month: ["month", "months"],
              year: ["year", "years"],
            };

      const plural = (n, key) => `${n} ${units[key][n === 1 ? 0 : 1]}`;

      // Requested format: "since 5 days..." / "depuis 5 jours..."
      if (diff < minute) return `${p} < 1 ${units.minute[0]}...`;
      if (diff < hour) return `${p} ${plural(Math.floor(diff / minute), "minute")}`;
      if (diff < day) return `${p} ${plural(Math.floor(diff / hour), "hour")}`;
      if (diff < week) return `${p} ${plural(Math.floor(diff / day), "day")}`;
      if (diff < month) return `${p} ${plural(Math.floor(diff / week), "week")}`;
      if (diff < year) return `${p} ${plural(Math.floor(diff / month), "month")}`;
      return `${p} ${plural(Math.floor(diff / year), "year")}...`;
    },
    [t, lang]
  );

  // --- roles helpers (keep existing business logic)
  const getUserRole = useCallback(
    (projectId) => {
      if (userGroups.includes("screenwriter")) return "screenwriter";
      const roles = rolesMap[projectId] || [];
      const r = roles.find((x) => x?.username?.toLowerCase() === username?.toLowerCase());
      return r?.role || null;
    },
    [rolesMap, userGroups, username]
  );

  const userIsContributor = useCallback(
    (projectId) => {
      const roles = rolesMap[projectId] || [];
      return roles.some((r) => r?.username?.toLowerCase() === username?.toLowerCase());
    },
    [rolesMap, username]
  );

  // --- load users for avatars (lazy, after first projects appear)
  useEffect(() => {
    if (!token || usersLoaded || usersLoading) return;
    if (!projects.length) return;

    setUsersLoading(true);
    fetch("/users", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) return [];
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return [];
        return r.json();
      })
      .then((arr) => {
        const map = {};
        (arr || []).forEach((u) => {
          if (u?.username) map[String(u.username).toLowerCase()] = u;
        });
        setUsersByUsername(map);
        setUsersLoaded(true);
      })
      .catch(() => {
        // still allow ui-avatars fallback
        setUsersLoaded(true);
      })
      .finally(() => setUsersLoading(false));
  }, [token, projects.length, usersLoaded, usersLoading]);

  const avatarFor = useCallback(
    (uName, labelForFallback = "") => {
      const key = String(uName || "").toLowerCase();
      const u = usersByUsername[key];

      const avatarUrl = u?.avatar_url || u?.avatarUrl || u?.avatar || u?.photo_url || u?.photoUrl;
      if (avatarUrl) return appendBust(normalizeUploadPath(String(avatarUrl)));

      const profilePath = u?.profilepath || u?.profilePath || u?.profile_path;
      if (profilePath) return appendBust(normalizeUploadPath(String(profilePath)));

      const fallbackName = labelForFallback || uName || "User";
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(String(fallbackName))}&background=dee2e6&color=495057`;
    },
    [usersByUsername]
  );

  const surnameFor = useCallback(
    (uName) => {
      const key = String(uName || "").toLowerCase();
      const u = usersByUsername[key];
      const s = u?.surname || u?.last_name || u?.lastName || u?.family_name || u?.familyName;
      return s ? String(s) : "";
    },
    [usersByUsername]
  );

  // --- paging: try /projects?limit&offset, otherwise fallback to /projects once
  const fetchProjectsPage = useCallback(
    async (pageIndex) => {
      if (!token) return { items: [], hasMore: false };

      const limit = PAGE_SIZE;
      const offset = pageIndex * PAGE_SIZE;

      // attempt server-side paging
      if (pagingSupportedRef.current) {
        try {
          const res = await fetch(`/projects?limit=${limit}&offset=${offset}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // If backend ignores params and returns ALL projects, detect by length >> limit
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) {
              // heuristic: if page>0 and server always returns same items, treat as non-paged
              const isProbablyAll = data.length > limit * 2;
              if (pageIndex === 0 && isProbablyAll) {
                pagingSupportedRef.current = false;
                setProjectsAllFallback(data);
                return { items: data.slice(0, limit), hasMore: data.length > limit, all: data };
              }
              return { items: data, hasMore: data.length === limit };
            }
          }

          // non-OK => fallback
          pagingSupportedRef.current = false;
        } catch {
          pagingSupportedRef.current = false;
        }
      }

      // fallback: fetch all once and slice
      const all = projectsAllFallback;
      if (Array.isArray(all)) {
        const slice = all.slice(offset, offset + limit);
        return { items: slice, hasMore: offset + limit < all.length, all };
      }

      const res2 = await fetch("/projects", { headers: { Authorization: `Bearer ${token}` } });
      const data2 = res2.ok ? await res2.json() : [];
      const allArr = Array.isArray(data2) ? data2 : [];
      setProjectsAllFallback(allArr);
      const slice2 = allArr.slice(offset, offset + limit);
      return { items: slice2, hasMore: offset + limit < allArr.length, all: allArr };
    },
    [token, projectsAllFallback]
  );

  const fetchRolesForProjects = useCallback(
    async (projectsArr) => {
      if (!token || !projectsArr?.length) return;

      // only load missing ones
      const missing = projectsArr.filter((p) => rolesMap[p.id] === undefined);
      if (!missing.length) return;

      const concurrency = 6;
      let i = 0;

      const runOne = async (p) => {
        try {
          const res = await fetch(`/projects/${p.id}/roles`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          return [p.id, Array.isArray(data) ? data : []];
        } catch {
          return [p.id, []];
        }
      };

      const results = {};
      const workers = new Array(Math.min(concurrency, missing.length)).fill(0).map(async () => {
        while (i < missing.length) {
          const idx = i++;
          const [pid, arr] = await runOne(missing[idx]);
          results[pid] = arr;
        }
      });

      await Promise.all(workers);

      setRolesMap((prev) => ({ ...prev, ...results }));
    },
    [token, rolesMap]
  );

  const resetAndLoadFirst = useCallback(async () => {
    if (!token) return;

    setLoadError("");
    setIsBootLoading(true);
    setIsPageLoading(false);
    setProjects([]);
    setRolesMap({});
    setPage(0);
    setHasMore(true);
    isLoadingRef.current = false;

    try {
      const r = await fetchProjectsPage(0);
      const firstItems = r?.items || [];
      setProjects(firstItems);
      setHasMore(Boolean(r?.hasMore));
      await fetchRolesForProjects(firstItems);
    } catch (e) {
      setLoadError(String(e?.message || "Failed to load projects"));
      setHasMore(false);
    } finally {
      setIsBootLoading(false);
    }
  }, [token, fetchProjectsPage, fetchRolesForProjects]);

  useEffect(() => {
    // Keep existing behavior: contrib mode cannot stay on All/Draft
    if (portfolioMode === "contrib" && (activeFilter === "All" || activeFilter === "Draft")) {
      setActiveFilter("Active");
    }
  }, [portfolioMode, activeFilter]);

  useEffect(() => {
    // when token changes, reload
    if (!token) return;
    resetAndLoadFirst();
  }, [token, portfolioMode]);

  const loadNextPage = useCallback(async () => {
    if (!token) return;
    if (isLoadingRef.current) return;
    if (!hasMore) return;

    isLoadingRef.current = true;
    setIsPageLoading(true);

    try {
      const next = page + 1;
      const r = await fetchProjectsPage(next);
      const newItems = r?.items || [];

      setProjects((prev) => {
        // dedupe by id (in case backend returns overlapping)
        const seen = new Set(prev.map((p) => p.id));
        const merged = [...prev];
        newItems.forEach((p) => {
          if (!seen.has(p.id)) merged.push(p);
        });
        return merged;
      });

      setPage(next);
      setHasMore(Boolean(r?.hasMore));
      await fetchRolesForProjects(newItems);
    } catch {
      setHasMore(false);
    } finally {
      setIsPageLoading(false);
      isLoadingRef.current = false;
    }
  }, [token, page, hasMore, fetchProjectsPage, fetchRolesForProjects]);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) loadNextPage();
      },
      { root: null, rootMargin: "900px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [loadNextPage]);

  // Bootstrap tooltips (scoped to page to avoid touching navbar)
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const els = root.querySelectorAll('[data-bs-toggle="tooltip"]');
    els.forEach((el) => {
      try {
        new Tooltip(el);
      } catch {
        // ignore
      }
    });
  }, [projects, lang]);

  /* -------------------- Visibility rules (unchanged) -------------------- */
  const visibleProjects = useMemo(() => {
    return (projects || []).filter((p) => {
      const status = p.status || "Draft";
      const isOwner = p.created_by?.toLowerCase() === username?.toLowerCase();
      const roles = rolesMap[p.id] || [];
      const isCoWriter = roles.some(
        (r) => r?.username?.toLowerCase() === username?.toLowerCase() && r?.role === "co-writer"
      );
      const isContributor = roles.some((r) => r?.username?.toLowerCase() === username?.toLowerCase());

      // --- 1. Filtrage par mode portefeuille ---
      if (portfolioMode === "master") {
        if (!isOwner) return false;
      } else if (portfolioMode === "contrib") {
        if (!isContributor || isOwner) return false;
        if (status === "Draft") return false;
      }

      // --- 2. Règles de visibilité existantes ---
      if (status === "Draft") return isOwner || isCoWriter;
      if (status === "Active") return isOwner || isContributor;
      return true;
    });
  }, [projects, rolesMap, username, portfolioMode]);

  const filteredProjects = useMemo(() => {
    if (activeFilter === "All") return visibleProjects;
    return visibleProjects.filter((p) => (p.status || "Draft") === activeFilter);
  }, [visibleProjects, activeFilter]);

  // When fallback-all is used, infinite scroll is purely client-side slicing
  const displayProjects = useMemo(() => {
    if (Array.isArray(projectsAllFallback)) {
      const max = (page + 1) * PAGE_SIZE;
      return filteredProjects.slice(0, max);
    }
    return filteredProjects;
  }, [filteredProjects, projectsAllFallback, page]);

  useEffect(() => {
    if (!Array.isArray(projectsAllFallback)) return;
    // reset slicing when filter changes
    setPage(0);
    setHasMore(filteredProjects.length > PAGE_SIZE);
  }, [activeFilter]);

  /* -------------------- Actions & routing (unchanged) -------------------- */
  const handleEdit = useCallback((id) => navigate(`/wizard?edit=true&id=${id}`), [navigate]);
  const handleShow = useCallback((id) => navigate(`/project/${id}/studio`), [navigate]);

  const openProjectFromCard = useCallback(
    (project) => {
      const st = project?.status || "Draft";
      if (st === "Draft" || st === "Brouillon") return handleEdit(project.id);
      if (st === "Clôturé" || st === "Terminé" || st === "Completed") return navigate(`/projects/${project.id}/recap-ui`);
      return handleShow(project.id);
    },
    [handleEdit, handleShow, navigate]
  );

  const renderProjectActions = useCallback(
    (project) => {
      const isOwner = project.created_by?.toLowerCase() === username?.toLowerCase();
      const status = project.status || "Draft";
      const visibility = project.visibility || "PRIVATE";

      const items = [];

      if (status === "Draft" && (isOwner || getUserRole(project.id) === "co-writer")) {
        items.push(
          <button
            type="button"
            key="edit"
            className="dropdown-item prod-dd-item"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(project.id);
            }}
          >
            <i className="bi bi-pencil-square" />
            <span>{t("editScenario")}</span>
          </button>
        );

        if (isOwner) {
          items.push(<div key="sep-draft" className="prod-dd-sep" />);
          items.push(
            <button
              type="button"
              key="delete"
              className="dropdown-item prod-dd-item danger"
              onClick={async (e) => {
                e.stopPropagation();

                const now = Date.now();

                // 1st click -> ask confirmation
                if (pendingDelete.id !== project.id || now > pendingDelete.expiresAt) {
                  setPendingDelete({ id: project.id, expiresAt: now + 5000 });
                  if (window.addToast) window.addToast(t("deleteConfirmHint"), "warning", 5000);
                  return;
                }

                // 2nd click within 5s -> delete
                setPendingDelete({ id: null, expiresAt: 0 });

                try {
                  const res = await fetch(`/projects/${project.id}`, {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (res.ok) {
                    setProjects((prev) => prev.filter((p) => p.id !== project.id));
                    if (window.addToast) window.addToast(`${t("delete")} ✅`, "success", 2500);
                  } else {
                    if (window.addToast) window.addToast("Delete failed", "danger", 3000);
                  }
                } catch {
                  if (window.addToast) window.addToast("Delete failed", "danger", 3000);
                }
              }}
            >
              <i className="bi bi-trash" />
              <span>{t("delete")}</span>
            </button>
          );
        }
      }

      if (status === "Active") {
        items.push(
          <button
            type="button"
            key="open"
            className="dropdown-item prod-dd-item"
            onClick={(e) => {
              e.stopPropagation();
              openProjectFromCard(project);
            }}
          >
            <i className="bi bi-camera-reels" />
            <span>{t("openStudio")}</span>
          </button>
        );
      }

      // Done/closed => recap
      if (status === "Clôturé" || status === "Completed" || status === "Terminé") {
        items.push(
          <button
            type="button"
            key="recap"
            className="dropdown-item prod-dd-item"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${project.id}/recap-ui`);
            }}
          >
            <i className="bi bi-card-checklist" />
            <span>{t("openRecap")}</span>
          </button>
        );
      }

      // Visibility rule preserved: private & not contributor -> request access
      if (visibility !== "PUBLIC" && !isOwner && !userIsContributor(project.id)) {
        const requestSent = requestSentProjects[project.id];

        items.push(
          <div key="locked" className="px-3 py-2 small text-muted">
            <i className="bi bi-lock-fill me-1" /> {t("restricted")}
          </div>
        );

        if (requestSent) {
          items.push(
            <div key="sent" className="px-3 pb-2 small text-success">
              {t("requestSent")}
            </div>
          );
        } else {
          items.push(
            <button
              type="button"
              key="request"
              className="dropdown-item prod-dd-item"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const res = await fetch(`/projects/${project.id}/request-access`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                      message: `Je souhaite rejoindre le projet ${project.title}`,
                    }),
                  });

                  if (res.ok) {
                    setRequestSentProjects((prev) => ({ ...prev, [project.id]: true }));
                    if (window.addToast) window.addToast(t("requestSent"), "success", 2500);
                  } else {
                    if (window.addToast) window.addToast("Request failed", "danger", 3000);
                  }
                } catch {
                  if (window.addToast) window.addToast("Request failed", "danger", 3000);
                }
              }}
            >
              <i className="bi bi-send" />
              <span>{t("requestAccess")}</span>
            </button>
          );
        }
      }

      return (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="prod-actions-trigger"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title={t("actions")}
          >
            <i className="bi bi-three-dots" />
          </button>

          <div className="dropdown-menu dropdown-menu-end prod-actions-menu">
            {items.length ? (
              items
            ) : (
              <span className="dropdown-item-text text-muted small">{t("noActions")}</span>
            )}
          </div>
        </div>
      );
    },
    [
      username,
      getUserRole,
      handleEdit,
      navigate,
      openProjectFromCard,
      pendingDelete,
      requestSentProjects,
      t,
      token,
      userIsContributor,
    ]
  );

  const renderProjectCard = useCallback(
    (project) => {
      const st = project.status || "Draft";
      const isDone = ["Clôturé", "Completed", "Terminé", "Termines"].includes(st);

      const owner = project.created_by ? String(project.created_by).toLowerCase() : "";
      const displayOwner = owner ? formatUsernameLabel(owner) : "";
      const displaySurname = owner ? surnameFor(owner) : "";
      const fullName = [displayOwner, displaySurname].filter(Boolean).join(" ");

      const identityImg = project.identity_image
        ? normalizeUploadPath(project.identity_image)
        : makeSeedImage(project.title, project.id);

      const videoSrc = `/output/project_${project.id}_final.mp4`;

      return (
        <div className="prod-card" onClick={() => openProjectFromCard(project)}>
          <div className="prod-top">
            {/* header row */}
          <div className="d-flex align-items-start justify-content-between" style={{ gap: 10 }}>
            <h6
              className="prod-title"
              title={project.title || t("titleFallback")}
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                handleShow(project.id);
              }}
            >
              {project.title || t("titleFallback")}
            </h6>

            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span
                className="prod-vis-badge"
                title={project.visibility === "PUBLIC" ? t("public") : t("private")}
                data-bs-toggle="tooltip"
              >
                <i className={project.visibility === "PUBLIC" ? "bi bi-globe2" : "bi bi-lock-fill"} />
              </span>
              {renderProjectActions(project)}
            </div>
          </div>
</div>

          {/* media */}
          <LazyMedia kind={isDone ? "video" : "image"} src={isDone ? videoSrc : identityImg} poster={identityImg} hoverAutoplay={Boolean(isDone)} />

          <div className="prod-bottom">
            {/* identity line */}
            <div className="prod-identity" title={owner ? fullName : ""}>
            <div
              className="prod-avatar"
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                if (owner) navigate(`/users/${owner}`);
              }}
              aria-label={owner ? `Open ${owner} profile` : "User"}
            >
              <img src={avatarFor(owner, fullName || owner)} alt={owner ? owner : "user"} loading="lazy" />
            </div>

            <div className="prod-name">
              {owner ? (
                <span
                  className="prod-username"
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${owner}`);
                  }}
                >
                  {formatUsernameLabel(owner)}
                  {displaySurname ? ` ${displaySurname}` : ""}
                </span>
              ) : (
                <span className="prod-username">{t("createdBy")}</span>
              )}
              <span className="prod-date">{formatSince(project.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
      );
},
    [
      avatarFor,
      formatSince,
      formatUsernameLabel,
      handleShow,
      navigate,
      openProjectFromCard,
      renderProjectActions,
      surnameFor,
      t,
    ]
  );

  const pills = useMemo(() => {
    const base = [
      { key: "All", label: t("all"), icon: "bi bi-grid-1x2" },
      { key: "Draft", label: t("draft"), icon: "bi bi-pencil-square" },
      { key: "Active", label: t("active"), icon: "bi bi-activity" },
      { key: "Clôturé", label: t("done"), icon: "bi bi-check2-circle" },
    ];
    // In contrib mode, Draft and All are forced away by existing logic. Hide Draft for clarity.
    if (portfolioMode === "contrib") return base.filter((x) => x.key !== "Draft");
    return base;
  }, [portfolioMode, t]);

  const empty = !isBootLoading && displayProjects.length === 0;

  return (
    <div className="page-wrap prod-pin" ref={containerRef}>
      <style>{styles}</style>

      {/* Topbar */}
      <div className="wv-topbar">
        <div className="wv-topbar-inner">
          {pills.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`filter-pill ${activeFilter === p.key ? "active" : ""}`}
              onClick={() => setActiveFilter(p.key)}
            >
              <i className={p.icon} />
              <span>{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* spacer to keep content below fixed topbar */}

      <div className="prod-main">
        {loadError ? (
          <div className="alert alert-danger rounded-4">{loadError}</div>
        ) : null}

        <div className="prod-masonry">
          {isBootLoading ? (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={`sk0-${i}`} />
              ))}
            </>
          ) : (
            <>
              {displayProjects.map((p) => (
                <div key={p.id} className="prod-masonry-item">
                  {renderProjectCard(p)}
                </div>
              ))}
              {isPageLoading
                ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={`skp-${i}`} />)
                : null}
            </>
          )}
        </div>

        {empty ? <p className="text-muted text-center mt-4">{t("none")}</p> : null}

        {/* sentinel for infinite scroll */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {/* fallback button (also helpful if browser blocks IO) */}
        {hasMore && !isBootLoading ? (
          <div className="d-flex justify-content-center my-4">
            <button type="button" className="btn btn-outline-dark rounded-pill px-4" onClick={loadNextPage}>
              {isPageLoading ? t("loading") : t("loadMore")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Production;
